<template>
  <div class="multi-camera-controls">
    <eewc-page-title
      class="multi-camera-controls__title"
      no-margin
      :title="t('Camera controls')"
    />
    <eewc-separator class="multi-camera-controls__separator" />
    <div class="multi-camera-controls__content">
      <eewc-button-common
        color="negative"
        type="clear"
        :loading="loading"
        @click="deleteAction"
        >{{ t('Delete camera') }}</eewc-button-common
      >
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { ApiMultiCameraWithIncludes } from '@eencloud/eewc-components/src/service/api-types';

import { t } from '@/plugins/i18n';
import { useCamerasStore, useMessagingStore, useMultiCameraSettingsStore } from '@/stores';
import { getBoldText } from '@/service/helpers';
import router from '@/service/router';

const props = defineProps<{
  cameraData?: ApiMultiCameraWithIncludes;
  cameraId: string;
  cameraName: string;
}>();

const camerasStore = useCamerasStore();
const messagingStore = useMessagingStore();
const multiCameraSettingsStore = useMultiCameraSettingsStore();

const loading = ref(false);

async function deleteMultiCamera() {
  try {
    loading.value = true;
    const status = await multiCameraSettingsStore.deleteMultiCamera(props.cameraId);
    if (status === 204) {
      messagingStore.addNotification({
        iconColor: 'positive',
        icon: '$icon_check_zero',
        title: t('Deleted'),
        body: t('The {multiCameraName} camera  was deleted from your Dashboard.', {
          multiCameraName: props.cameraName,
        }),
      });
      router.push({ name: 'Dashboard' });
    }
  } catch (error) {
    console.log(error);
  } finally {
    loading.value = false;
  }
}

function showConfirmOnDelete() {
  messagingStore.showCustomConfirmationDialog({
    title: t('Delete camera?'),
    callback: async () => await deleteMultiCamera(),
    bodyText: t('Are you sure you want to delete the multi-camera with the name {multiCameraName}?', {
      multiCameraName: getBoldText(props.cameraName),
    }),
    buttonText: t('Delete camera'),
    doNotShowNotification: true,
  });
}

function showWarningDialog() {
  messagingStore.showCustomConfirmationDialog({
    title: t('Delete camera?'),
    cancelText: t('Go back'),
    bodyText: t('Please remove all views connected to the {cameraName} camera before deleting it.', {
      cameraName: getBoldText(props.cameraName),
    }),
    doNotShowNotification: false,
    hideConfirmButton: true,
  });
}

async function deleteAction() {
  loading.value = true;
  const { cameras } = await camerasStore.getPagedCameras({
    multiCameraId: props.cameraId,
    pageSize: 1,
  });
  loading.value = false;

  return cameras.length ? showWarningDialog() : showConfirmOnDelete();
}
</script>

<style lang="scss" scoped>
@import '@/styles/public/main.scss';

.multi-camera-controls {
  padding-top: 32px;
  &__separator {
    margin-top: 0px !important;
  }

  &__content {
    padding-left: 54px;
  }

  &__banner {
    @include body-2;
    margin-bottom: 20px;
  }
}
</style>
